import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import * as moment from 'moment';
import { CharterDAO } from '../../../../core/services/dao/charterDAO';
import { Charter } from 'anchor-shared/lib/model/entity/charter';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ManualPaymentModalComponent } from '../../../shared/modals/manual-payment/manual-payment-modal.component';
import { TransactionChickenTestComponent } from '../../../shared/modals/transaction-chicken-test/transaction-chicken-test.component';
import { NumberFormatterService } from '../../../../core/services/number.formatter.service';
import { ConstantMan } from '../../../../core/services/constantMan';
import {
  getRoundedFullPaymentAmount,
  getRoundedRemainingPaymentAmount,
} from '../../../../core/pure-functions/charter-pricing-helpers';
import { PaymentDAO } from '../../../../core/services/dao/paymentDAO';
import { Payment } from 'anchor-shared/lib/model/entity/payment';
import { ContainsSubscriptions } from '../../../../core/services/interfaces/contains-subscriptions';
import { BehaviorSubject, Subscription } from 'rxjs';
import { unsubscribeFromAll } from '../../../../core/pure-functions/rx-helper';
import { CaptainGigDAO } from '../../../../core/services/dao/captainGigDAO';
import { getPromoAmountFromCharter } from '../../../../core/pure-functions/promo-codes/promo-code-helper';
import { VesselAvailabilityDAO } from '../../../../core/services/dao/VesselAvailabilityDAO';
import { take } from 'rxjs/operators';
import { PaymentProcessor } from 'anchor-shared/lib/model/enum/payment-processor';
import { PaymentType } from 'anchor-shared/lib/model/enum/payment-type';
import { CometChatDAO } from '../../../../core/services/dao/cometChatDAO';

@Component({
  selector: 'app-invoicing',
  templateUrl: './invoicing.component.html',
  styleUrls: ['./invoicing.component.scss'],
})
export class InvoicingComponent
  implements OnInit, OnDestroy, ContainsSubscriptions
{
  payments: any[];
  @Input() charter: Charter;
  @Input() runInit: BehaviorSubject<boolean>;

  deposit: any;
  remaining: any;
  invoicePaymentMethod: string;
  depositInvoicePaymentMethod: string;
  fullPaymentInvoicePaymentMethod: string;
  full: any;
  transId: string;
  notes: string;
  depositRequestSent = false;
  fullPaymentRequestSent = false;
  depositAmount = null;
  depositSaved = true;
  services: any;
  discounts: any;
  serviceTotal = 0;
  discountTotal = 0;
  serviceString = '';
  discountString = '';
  loading = false;
  subs: Subscription[] = [];
  @Output() sendDepositRequest = new EventEmitter();
  @Output() sendRemainingPaymentRequest = new EventEmitter();
  @Output() sendFullPaymentRequest = new EventEmitter();
  @Output() _initPayments = new EventEmitter();
  @Output() emitPayments = new EventEmitter<Payment[]>();
  @Output() updateCharter = new EventEmitter<Charter>();
  remainingBalanceLoading = false;

  constructor(
    private charterDao: CharterDAO,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private chickenTestModal: MatDialog,
    private numFormatter: NumberFormatterService,
    private paymentDAO: PaymentDAO,
    private formatter: NumberFormatterService,
    private captainGigDAO: CaptainGigDAO,
    private vesselAvailabilityDAO: VesselAvailabilityDAO,
    private cometChatDAO: CometChatDAO,
  ) {}

  ngOnInit() {
    this.runLoadInit();
    this.initWatcher();
  }

  initWatcher(): void {
    if (this.runInit) {
      const loadSub = this.runInit.subscribe((loadState) => {
        if (loadState) {
          this.runLoadInit();
          if (this.fullPaymentRequestSent) {
            this.fullPaymentRequestSent = !this.fullPaymentRequestSent;
          }
          if (this.depositRequestSent) {
            this.depositRequestSent = !this.depositRequestSent;
          }
        }
      });
      this.subs.push(loadSub);
    }
  }

  runLoadInit(): void {
    this.getCharter();
    this.initServices();
    this.initDiscounts();
  }

  showManualPaymentModal(payment) {
    this.dialog
      .open(ManualPaymentModalComponent, { data: payment })
      .afterClosed()
      .subscribe((data) => {
        if (data) {
          if (
            this.depositInvoicePaymentMethod !== 'other' &&
            this.invoicePaymentMethod !== 'other' &&
            this.transId
          ) {
            this.charterDao
              .getStripeTransaction(this.transId)
              .subscribe((transaction) => {
                this.createOrUpdatePayment(data, transaction);
              });
          } else {
            this.createOrUpdatePayment(data);
          }
        }
      });
  }

  createOrUpdatePayment(modalResponse, transaction?) {
    let _transaction = null;
    if (transaction) {
      _transaction = transaction;
    }
    if (modalResponse === PaymentType.DEPOSIT) {
      this.createManualDeposit(_transaction);
    } else if (modalResponse === PaymentType.FULL) {
      this.createManualFullPayment(_transaction);
    } else if (modalResponse === PaymentType.REMAINING) {
      this.createManualRemainingPayment(_transaction);
    } else if (modalResponse.id) {
      this.updatePayment(modalResponse, _transaction);
    }
    this.clearFields();
  }

  clearFields(): void {
    this.transId = '';
    this.notes = '';
    this.depositInvoicePaymentMethod = '';
  }

  initServices() {
    if (this.charter.services && typeof this.charter.services === 'string') {
      this.services = this.numFormatter.parseArrayFromJson(
        this.charter.services,
      );
    } else {
      this.services = this.charter.services;
    }

    if (this.services) {
      this.services.forEach((el) => {
        this.serviceTotal += parseFloat(el.cost);
        this.serviceString += el.name + ' - $' + el.cost + '<br>';
      });
    }
  }

  initDiscounts() {
    this.discounts = this.numFormatter.parseArrayFromJson(
      this.charter.discounts,
    );
    if (this.discounts) {
      this.discounts.forEach((el) => {
        this.discountTotal += parseFloat(el.amount);
        this.discountString += el.name + ' - $' + el.amount + '<br>';
      });
    }
  }

  createDepositPaymentObj(
    amount: number,
    transaction?: SimpleTransaction,
  ): any {
    const payment = {
      type: PaymentType.DEPOSIT,
      name: 'Deposit Payment',
      paid: false,
      failed: false,
      manual: false,
      charterId: this.charter.id,
      amount: transaction ? transaction.amount : amount,
      transactionCreatedAt: transaction ? transaction.createdDateString : null,
      dateSent: new Date().getTime(),
    };
    if (this.depositInvoicePaymentMethod === 'stripe') {
      payment['transactionId'] = this.transId;
      payment['paymentIntentId'] = this.transId;
      payment['processor'] = PaymentProcessor.STRIPE;
    }
    if (this.depositInvoicePaymentMethod === 'other') {
      payment['notes'] = this.notes;
      payment['transactionCreatedAt'] = moment()
        .utc()
        .format('YYYY-MM-DD HH:mm');
    }
    return payment;
  }

  createRemainingPaymentObj(amount, transaction?: SimpleTransaction): any {
    const payment = {
      type: PaymentType.REMAINING,
      name: 'Remaining Balance Payment',
      paid: false,
      failed: false,
      manual: false,
      charterId: this.charter.id,
      amount: transaction ? transaction.amount : amount,
      transactionCreatedAt: transaction ? transaction.createdDateString : null,
      dateSent: new Date().getTime(),
    };
    if (this.invoicePaymentMethod === 'stripe') {
      payment['transactionId'] = this.transId;
      payment['paymentIntentId'] = this.transId;
      payment['processor'] = PaymentProcessor.STRIPE;
    }
    if (this.invoicePaymentMethod === 'other') {
      payment['notes'] = this.notes;
      payment['transactionCreatedAt'] = moment()
        .utc()
        .format('YYYY-MM-DD HH:mm');
    }
    return payment;
  }

  createManualDeposit(transaction?: SimpleTransaction) {
    const amount = this.charter.depositAmount;
    let payment;
    if (transaction) {
      payment = this.createDepositPaymentObj(amount, transaction);
    } else {
      payment = this.createDepositPaymentObj(amount);
    }
    payment.manual = true;
    payment.paid = true;
    payment.transactionCreatedAt = moment().utc().format('YYYY-MM-DD HH:mm');
    this.charterDao.sendPaymentRequest(payment).subscribe((data) => {
      this.payments.push(data);
      this.setPayments();
      this.charter.paymentReceived = true;
      this.charter.paymentRequested = true;
      this.charter.depositStatus = 'PAID';
      this.charter.open = true;
      this.updateCharter.emit(this.charter);
      this.charterDao.serverUpdate(this.charter).subscribe((updated) => {
        this.updateCharterAvailability();
      });
    });
  }

  createManualRemainingPayment(transaction?: SimpleTransaction) {
    const promoAmount = getPromoAmountFromCharter(this.charter);
    const amount = getRoundedRemainingPaymentAmount(
      this.charter,
      promoAmount,
    ).toString();
    let payment;
    if (transaction) {
      payment = this.createRemainingPaymentObj(amount, transaction);
    } else {
      payment = this.createRemainingPaymentObj(amount);
    }
    payment.manual = true;
    payment.paid = true;
    this.charterDao.sendPaymentRequest(payment).subscribe((data) => {
      this.payments.push(data);
      this.setPayments();
      this.charter.paymentReceived = true;
      this.charter.paymentRequested = true;
      this.charter.remainderStatus = 'PAID';
      this.charter.open = true;
      this.charterDao.serverUpdate(this.charter).subscribe((updated) => {
        this.updateCharterAvailability();
      });
      this.getCharter();
    });
  }

  createManualFullPayment(transaction?: SimpleTransaction) {
    const promoAmount = getPromoAmountFromCharter(this.charter);
    const amount = getRoundedFullPaymentAmount(
      this.charter,
      promoAmount,
    ).toString();
    let payment;
    if (transaction) {
      payment = this.createFullPaymentObj(amount, transaction);
    } else {
      payment = this.createFullPaymentObj(amount);
    }
    payment.manual = true;
    payment.paid = true;
    this.charterDao.sendPaymentRequest(payment).subscribe((data) => {
      this.payments.push(data);
      this.setPayments();
      this.charter.paymentReceived = true;
      this.charter.paymentRequested = true;
      this.charter.remainderStatus = 'PAID';
      this.charter.open = true;
      this.updateCharter.emit(this.charter);
      this.charterDao.serverUpdate(this.charter).subscribe((updated) => {
        this.updateCharterAvailability();
        this.cometChatDAO
          .updateGroupCharterStatus(
            this.charter.id,
            'admin',
            'createManualFullPayment',
          )
          .subscribe();
      });
    });
  }

  createDepositRequest() {
    const amount = this.charter.depositAmount;
    const payment = this.createDepositPaymentObj(amount);
    this.chickenTestModal
      .open(TransactionChickenTestComponent, { data: amount })
      .afterClosed()
      .subscribe((data) => {
        if (data) {
          this.loading = true;
          const availabilityUpdateSub = this.charterDao
            .updateFromPartial(this.charter.id, {
              availabilityStatus: 'Available',
            })
            .subscribe();
          this.subs.push(availabilityUpdateSub);
          this.charterDao
            .sendPaymentRequest(payment)
            .subscribe((createdPayment) => {
              const body = {
                charterId: this.charter.id,
                paymentId: createdPayment.id,
              };
              this.sendDepositEmail(body);
            });
        }
      });
  }

  handleDepositChange() {
    this.depositSaved = false;
  }

  saveDepositAmount() {
    this.charter.depositAmount = this.numFormatter.unMaskPrice(
      this.depositAmount,
    );
    this.charterDao
      .updateFromPartial(this.charter.id, {
        depositAmount: this.charter.depositAmount,
      })
      .subscribe((data) => {
        this.charter.depositAmount = this.depositAmount;
        this.depositSaved = true;
      });
  }

  initPayments() {
    this.payments.forEach((payment) => {
      if (payment.type === PaymentType.DEPOSIT && !payment.failed) {
        this.depositRequestSent = true;
      }
      if (payment.type === PaymentType.FULL && !payment.failed) {
        this.fullPaymentRequestSent = true;
      }
    });
  }

  updatePayment(payment, transaction?: SimpleTransaction) {
    if (payment) {
      const id = payment.id;
      let updates;
      if (transaction) {
        updates = this.getUpdatedPaymentDetails(payment, transaction);
      } else {
        updates = this.getUpdatedPaymentDetails(payment);
      }
      this.charterDao.updatePayment(id, updates).subscribe((data) => {
        this.invoicePaymentMethod = undefined;
        this.updateCharterPaymentStatus(payment);
        this.charterDao
          .updateFromPartial(this.charter.id, {
            paymentReceived: this.charter.paymentReceived,
            depositStatus: this.charter.depositStatus,
            remainderStatus: this.charter.remainderStatus,
          })
          .subscribe((res) => {
            this.openSnackbar('Manual payment updates successful');
            this.cometChatDAO
              .updateGroupCharterStatus(
                this.charter.id,
                'admin',
                'createManualFullPayment',
              )
              .subscribe();
          });
      });
    }
  }

  updateCharterPaymentStatus(payment) {
    this.charter.paymentReceived = true;
    if (payment.type === PaymentType.DEPOSIT) {
      this.charter.depositStatus = 'PAID';
    } else if (payment.type === PaymentType.FULL) {
      this.charter.remainderStatus = 'PAID';
    }
  }

  getUpdatedPaymentDetails(payment, transaction?: SimpleTransaction): any {
    if (payment.type === PaymentType.DEPOSIT) {
      if (this.depositInvoicePaymentMethod === 'stripe') {
        payment.transactionId = this.transId;
        payment.paymentIntentId = this.transId;
        payment.processor = PaymentProcessor.STRIPE;
      }
      if (this.depositInvoicePaymentMethod === 'other') {
        payment.notes = this.notes;
        payment.transactionCreatedAt = moment()
          .utc()
          .format('YYYY-MM-DD HH:mm');
      }
    }
    if (payment.type === PaymentType.FULL) {
      if (this.fullPaymentInvoicePaymentMethod === 'stripe') {
        payment.transactionId = this.transId;
        payment.paymentIntentId = this.transId;
        payment.processor = PaymentProcessor.STRIPE;
      }
      if (this.fullPaymentInvoicePaymentMethod === 'other') {
        payment.notes = this.notes;
        payment.transactionCreatedAt = moment()
          .utc()
          .format('YYYY-MM-DD HH:mm');
      }
    }
    delete payment.id;
    payment.failed = false;
    payment.paid = true;
    payment.manual = true;
    if (transaction) {
      payment.amount = transaction.amount;
      payment.transactionCreatedAt = transaction.createdDateString;
    }
    return payment;
  }

  sendDepositEmail(body) {
    this.charterDao.sendUserDepositEmail(body).subscribe((response) => {
      this.openSnackbar(
        `Deposit request successfully sent to ${this.charter.customerEmail}`,
      );
      this.getCharter();
      this.loading = false;
    });
  }

  setPayments() {
    this.payments.forEach((el) => {
      if (el.type === PaymentType.DEPOSIT) {
        this.deposit = el;
      }
      if (el.type === PaymentType.REMAINING) {
        this.remaining = el;
      }
      if (el.type === PaymentType.FULL) {
        this.full = el;
      }
    });
    this.initPayments();
    this.payments.forEach((payment) => {
      payment.transactions = this.numFormatter.parseJsonIfString(
        payment.transactions,
      );
    });
    this.emitPayments.emit(this.payments);
  }

  getCharter() {
    const charterSub = this.charterDao
      .findById(this.charter.id)
      .subscribe((data) => {
        const charter = data;
        this.payments = charter.payments;
        this.depositAmount = charter.depositAmount;
        this.charter = charter;
        this.updateCharter.emit(this.charter);
        this.setPayments();
      });
    this.subs.push(charterSub);
  }

  openSnackbar(message: string) {
    this.snackBar.open(message, null, {
      duration: 5000,
      verticalPosition: 'top',
    });
  }

  createFullPaymentObj(amount, transaction?: SimpleTransaction): any {
    const payment = {
      type: PaymentType.FULL,
      charterId: this.charter.id,
      name: 'Full Payment',
      paid: false,
      failed: false,
      amount: transaction ? transaction.amount : amount,
      transactionCreatedAt: transaction ? transaction.createdDateString : null,
      dateSent: new Date().getTime(),
    };
    if (this.fullPaymentInvoicePaymentMethod === 'stripe') {
      payment['transactionId'] = this.transId;
      payment['paymentIntentId'] = this.transId;
      payment['processor'] === PaymentProcessor.STRIPE;
    }
    if (this.fullPaymentInvoicePaymentMethod === 'other') {
      payment['notes'] = this.notes;
      payment['transactionCreatedAt'] = moment()
        .utc()
        .format('YYYY-MM-DD HH:mm');
    }
    return payment;
  }

  _sendFullPaymentRequest(_payment?: Payment) {
    const promoAmount = getPromoAmountFromCharter(this.charter);
    const amount = getRoundedFullPaymentAmount(this.charter, promoAmount);
    const payment = this.createFullPaymentObj(amount);
    this.chickenTestModal
      .open(TransactionChickenTestComponent, { data: payment['amount'] })
      .afterClosed()
      .subscribe((data) => {
        if (data) {
          this.loading = true;
          const availabilityUpdateSub = this.charterDao
            .updateFromPartial(this.charter.id, {
              availabilityStatus: 'Available',
            })
            .subscribe();
          this.subs.push(availabilityUpdateSub);
          if (_payment) {
            const body = { charterId: this.charter.id, paymentId: _payment.id };
            if (!_payment.paid) {
              this.paymentDAO
                .updateFromPartial(_payment.id, {
                  amount: amount,
                })
                .subscribe((updatedPayment) => {
                  this.sendFullPaymentEmail(body);
                });
            } else {
              this.openSnackbar(
                'This charter has already been paid for.  No email was sent.',
              );
            }
          } else {
            this.charterDao
              .sendPaymentRequest(payment)
              .subscribe((createdPayment) => {
                const body = {
                  charterId: this.charter.id,
                  paymentId: createdPayment.id,
                };
                this.sendFullPaymentEmail(body);
              });
          }
        }
      });
  }

  sendFullPaymentEmail(body) {
    this.charterDao.sendUserFullPaymentEmail(body).subscribe((response) => {
      this.openSnackbar(
        `Full payment request successfully sent to ${this.charter.customerEmail}`,
      );
      this.loading = false;
      this.getCharter();
    });
  }

  _sendDepositRequest(deposit) {
    this.sendDepositRequest.emit(deposit.id);
  }

  _sendRemainingPaymentRequest() {
    // Update charter in case any addons have been made
    this.remainingBalanceLoading = true;
    const charterSub = this.charterDao
      .loadById(this.charter.id)
      .subscribe((data) => {
        this.charter = data;
        console.log(this.charter.services);
        this.services = this.formatter.parseArrayFromJson(
          this.charter.services,
        );
        const promoAmount = getPromoAmountFromCharter(this.charter);
        const amount = getRoundedRemainingPaymentAmount(
          this.charter,
          promoAmount,
        );
        console.log(amount, this.services);

        const transactionChickenTestSub = this.chickenTestModal
          .open(TransactionChickenTestComponent, { data: amount })
          .afterClosed()
          .subscribe((transactionData) => {
            if (transactionData) {
              this.loading = true;
              const sendRemainingBalanceSub = this.charterDao
                .sendRemainingBalanceRequest(this.charter.id)
                .subscribe((response) => {
                  this.remainingBalanceLoading = false;
                  if (response.res) {
                    if (
                      (this.services && this.services.length) ||
                      (this.discounts && this.discounts.length)
                    ) {
                      const updateCASub = this.charterDao
                        .updateFromPartial(this.charter.id, {
                          agreementCreatedDate: null,
                          agreementSignedDate: null,
                          agreementUploadDate: null,
                          agreementUrl: null,
                          signedAgreementUrl: null,
                        })
                        .subscribe(() => {
                          const sendCharterAgreementSub = this.charterDao
                            .sendAgreementEmailToCustomer(this.charter.id)
                            .subscribe((updated) => {
                              this.openSnackbar(
                                'Remaining charge successful. Resent Charter Agreement.',
                              );
                              this.loading = false;
                            });
                          this.subs.push(sendCharterAgreementSub);
                        });
                      this.subs.push(updateCASub);
                    }
                    this.getCharter();
                  } else if (!response.res) {
                    this.openSnackbar(
                      `${response.message} - Remaining charge unsuccessful, please try again or contact customer`,
                    );
                    this.loading = false;
                  }
                });
              this.subs.push(sendRemainingBalanceSub);
            } else {
              this.remainingBalanceLoading = false;
            }
          });
        this.subs.push(transactionChickenTestSub);
      });
    this.subs.push(charterSub);
  }

  formatDate(date) {
    return moment(date).format('MM/DD/YYYY');
  }

  getRemainingBalanceDate() {
    return moment(this.charter.date).subtract(7, 'days').format('MM/DD/YYYY');
  }

  ngOnDestroy(): void {
    unsubscribeFromAll(this.subs);
  }

  private leadToOpenCharterSync(): void {
    console.log('leadToOpenCharterSync');
    this.captainGigDAO
      .syncGigsForCharterEdit(this.charter.id, this.charter.vesselId)
      .subscribe((gigSync) => {
        console.log('gig sync');
        console.log(gigSync);
      });
  }

  private updateCharterAvailability(): void {
    this.vesselAvailabilityDAO
      .updateSingleDayAvailabilityForVessel(
        this.charter.vesselId,
        this.charter.date,
      )
      .pipe(take(1))
      .subscribe(() => {
        this.openSnackbar('Payments updated');
        this.leadToOpenCharterSync();
      });
  }
}

export interface SimpleTransaction {
  amount: number;
  createdDateString: string;
}
